import type { QueryClient } from "@tanstack/react-query";

import { getFirstLandingPath, getLandingJobId } from "@/client/libs/attribution";
import { queryKeys } from "@/client/queries/keys";
import { fetchJobProfiles } from "@/client/services/job-profile";

import { landingJobId as jobIdFromLandingPath } from "../jobs/landing-job";

/** Same shape the attribution capture and the server accept. */
const JOB_ID_PATTERN = /^[\da-z]+$/;

/** Key under which the wizard keeps the landing job inside its saved progress data. */
export const WIZARD_LANDING_JOB_KEY = "landingJobId";

export const validJobId = (value: unknown): string | null =>
  typeof value === "string" && JOB_ID_PATTERN.test(value) ? value : null;

/**
 * Job id parsed from the first page the visitor ever landed on, for people who
 * read a job page but signed up through another link (logo, footer) and so have
 * no explicit `?jobId`. Pure read; nothing is consumed.
 */
export const firstLandingJobId = (): string | null =>
  validJobId(jobIdFromLandingPath(getFirstLandingPath()));

/**
 * The job a visitor came for, without consuming anything. Order: the id this
 * browser captured from the CTA, the id the server saved with their onboarding
 * progress (another device or a cleared browser), then the first-landing path.
 * An invalid or malformed value at any layer is skipped.
 */
export const peekLandingJobId = (savedJobId?: unknown): string | null =>
  validJobId(getLandingJobId()) ?? validJobId(savedJobId) ?? firstLandingJobId();

/**
 * Landing job for the "Start hunting" finish when the explicit local id was
 * already consumed: falls back to the saved id, then the first-landing path.
 */
export const fallbackLandingJobId = (savedJobId?: unknown): string | null =>
  validJobId(savedJobId) ?? firstLandingJobId();

/**
 * After a user who skipped the résumé import creates their starter résumé, open
 * the existing tailor deep link for the job they came for, but only once a job
 * profile exists (the deep link cannot tailor without one and would otherwise
 * show its error screen). With no profile yet the id stays stored, so the
 * "job you came for" card on the jobs feed still finds it later.
 *
 * @returns true when the visitor was sent to the tailor deep link
 */
export const openLandingJobAfterStarterResume = async (
  queryClient: QueryClient,
  navigate: (to: string) => void | Promise<void>,
  rawJobId: unknown,
  clearLandingJob: () => void,
): Promise<boolean> => {
  const jobId = validJobId(rawJobId);
  if (!jobId) return false;
  try {
    const profiles = await queryClient.fetchQuery({
      queryKey: queryKeys.jobProfiles.list(),
      queryFn: fetchJobProfiles,
      staleTime: 0,
    });
    if (profiles.length === 0) return false;
  } catch {
    return false;
  }
  clearLandingJob();
  void navigate(`/jobs/tailor/${jobId}`);
  return true;
};
